import { BezierCurve, Box, IBox, Vector2 } from '@adaptiva/core/space';
import { AdaColor, handleColorTypes, ThemeColor } from '@adaptiva/core/themes';
import { ViewboxService } from '@adaptiva/widgets/viewbox';
import { Component, computed, effect, EffectRef, ElementRef, inject, input, InputSignal, Signal, untracked } from '@angular/core';

@Component({
	selector: 'svg[ada-llm-history-node-connector]',
	templateUrl: './llm-history-node-connector.component.html',
	styleUrl: './llm-history-node-connector.component.scss',
})
export class LlmHistoryNodeConnectorComponent {

	public readonly from: InputSignal<IBox> = input.required();

	public readonly to: InputSignal<IBox> = input.required();

	public readonly cameraZoom: InputSignal<float> = input(0);

	public cameraZoomValue: float = 1;

	public readonly lineColor: InputSignal<ThemeColor | AdaColor> = input('foreground');

	public readonly lineWidth: InputSignal<number> = input(2);

	protected readonly elementRef: ElementRef<SVGElement> = inject(ElementRef);

	public readonly viewboxService: ViewboxService = inject(ViewboxService);

	protected readonly updateCameraEffect: EffectRef = effect(() => {
		this.viewboxService.redrawTick();
		this.cameraZoomValue = untracked(() => this.cameraZoom());
	});

	protected get parentBox(): Box {
		const _container: DOMRect = this.elementRef.nativeElement.parentElement.getBoundingClientRect();

		return new Box({
			topLeft: new Vector2(_container.left, _container.top),
			bottomRight: new Vector2(_container.right, _container.bottom),
		});
	}

	protected readonly strokeColor: Signal<string> = computed(() => {
		return handleColorTypes(this.lineColor());
	});

	protected readonly curve: Signal<string> = computed(() => {
		this.viewboxService.redrawTick();
		const _start: Vector2 = this.start();
		const _end: Vector2 = this.end();
		const _curve: BezierCurve = new BezierCurve({
			start: _start,
			end: _end,
			controls: [
				new Vector2(_start.x, _end.y),
				new Vector2(_end.x, _start.y),
			]
		});

		return _curve.toString();
	});

	protected start(): Vector2 {
		const _from: IBox = this.from();
		const _container: IBox = this.parentBox;
		const _start: Vector2 = new Vector2(
			_from.center.x - _container.topLeft.x + window.scrollX,
			_from.bottomRight.y - _container.topLeft.y + window.scrollY,
		);

		return _start.divide(this.cameraZoomValue);
	}

	protected end(): Vector2 {
		const _to: IBox = this.to();
		const _container: IBox = this.parentBox;
		const _end: Vector2 = new Vector2(
			_to.center.x - _container.topLeft.x + window.scrollX,
			_to.topLeft.y - _container.topLeft.y + window.scrollY,
		);

		return _end.divide(this.cameraZoomValue);
	}

}
